import React, { useEffect, useState } from 'react';
import { Flex, Input, Spin, Table, Tag, Typography } from '@duxton-web/ui';
import { darkStyles, JsonView } from 'react-json-view-lite';

import { AdaptiveIcon } from '../../../common/Icons/AdaptiveIcon';
import { VerticalDividerIcon } from '../../../common/Icons/VerticalDividerIcon';
import { AdaptiveExperiment, AdaptiveTrial } from '../../../lib/PlannerDTOs';
import { CardCollapse } from '../common/CardCollapse';

import 'react-json-view-lite/dist/index.css';

import { SearchUniversal, SubtractUniversal } from '@duxton-web/icons';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useLocalStorage } from 'react-use';

import Service from '../../../lib/App';
import { EXPERIMENT_FILTER_QUERY_SS_KEY } from '../../ExperimentFormV2/helper/consts';
import {
  getStatusTagConfig,
  renderStatusColumn,
} from '../common/DashboardColumn';
import {
  calculateDaysDifference,
  extractCompletionProgress,
  sortString,
  transformMilliSecondTime,
  transformProgress,
} from '../helper/utils';
import { ObjectDetailPage } from '@/common/components';
import { getTimezoneDisplay } from '@/utils/dateTime';
import { appendEnvParam } from '@/utils/navigation';

const AdaptiveTrialPage = ({
  setOpenTrialDrawer,
  allTrialsByTrialName,
  owner,
}) => {
  const trial = allTrialsByTrialName[0];
  const fullTrialName = `${trial?.domain}.${trial?.adaptive_experiment_name}.${trial?.client}.${trial?.trial_name}`;
  const { color, content, tooltip } = getStatusTagConfig(trial?.state);

  return (
    <ObjectDetailPage
      className="experimentPageV2-subPage"
      breadcrumbs={[
        {
          path: '',
          title: fullTrialName,
        },
      ]}
      icon={<AdaptiveIcon />}
      headerTags={[
        {
          color: color,
          content: content,
          tooltip: tooltip,
          bordered: false,
          size: 'xsmall',
        },
      ]}
      infos={[
        {
          title: 'Type',
          content: 'Adaptive Experiment',
        },
        {
          title: 'Creator',
          content: owner,
        },
      ]}
      onBack={() => setOpenTrialDrawer(false)}
    >
      <div
        style={{
          padding: '0px 16px',
          height: '100%',
          overflow: 'scroll',
          paddingBottom: '70px',
        }}
      >
        <CardCollapse
          title="Trial Setup"
          subtitle={`This is the JSON view for all the arms of trial ${allTrialsByTrialName[0]?.trial_name}`}
          defaultOpen={true}
        >
          <JsonView
            data={allTrialsByTrialName}
            shouldInitiallyExpand={(level) => (level < 2 ? true : false)}
            style={customStyles}
          />
        </CardCollapse>
      </div>
    </ObjectDetailPage>
  );
};

export function AdaptiveDetailDrawer({
  adaptiveExperiment,
}: {
  adaptiveExperiment: AdaptiveExperiment;
  handleClone?: (adaptiveExperiment: AdaptiveExperiment) => void;
}) {
  const [openTrialDrawer, setOpenTrialDrawer] = useState<boolean>(false);
  const [allTrialsByTrialName, setAllTrialsByTrialName] = useState<any>([]);
  const [filterQuery, setFilterQuery] = useLocalStorage(
    EXPERIMENT_FILTER_QUERY_SS_KEY,
  );

  const { isLoading, data, error } = useQuery({
    queryKey: ['adaptiveTrials', adaptiveExperiment?.name],
    queryFn: () => {
      return Service.planner.fetchAdaptiveExperimentTrials(
        adaptiveExperiment?.name,
      );
    },
    retry: false,
  });

  const [tableData, setTableData] = useState(data);
  const partitionData = _.groupBy(tableData, (record) => record?.trial_name);
  const uniqueTrialData = Object.values(partitionData).map(
    (trials) => trials[0],
  );

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const onSearch = (input: string) => {
    if (input === '') {
      return setTableData(data);
    }
    const filteredData = data?.filter((datum: AdaptiveTrial) =>
      datum?.trial_name.includes(input),
    );
    return setTableData(filteredData);
  };

  if (openTrialDrawer) {
    return (
      <AdaptiveTrialPage
        setOpenTrialDrawer={setOpenTrialDrawer}
        owner={adaptiveExperiment?.owner}
        allTrialsByTrialName={allTrialsByTrialName}
      />
    );
  }

  function renderAdaptiveDetail() {
    const { color, content, tooltip } = getStatusTagConfig(
      adaptiveExperiment?.state,
    );

    return (
      <ObjectDetailPage
        icon={<AdaptiveIcon />}
        breadcrumbs={[
          {
            path: filterQuery
              ? appendEnvParam(`/t/experiments?${filterQuery}`)
              : appendEnvParam(`/t/experiments`),
            onClick: () => {
              setFilterQuery('');
            },
            title: 'Experiment',
          },
          {
            path: '',
            title: adaptiveExperiment?.name,
          },
        ]}
        headerTags={[
          {
            color: color,
            content: content,
            tooltip: tooltip,
            bordered: false,
            size: 'xsmall',
          },
        ]}
        infos={[
          {
            title: 'Type',
            content: 'Adaptive Experiment',
          },
          {
            title: 'Created By',
            content: adaptiveExperiment?.owner,
          },
          {
            title: 'Domain',
            content: adaptiveExperiment?.experiment_template?.domain ? (
              adaptiveExperiment?.experiment_template?.domain
            ) : (
              <SubtractUniversal />
            ),
          },
        ]}
        className="experimentPageV2-subPage"
      >
        <Flex
          vertical
          style={{
            padding: 24,
          }}
        >
          <div>
            <div className="trials-container-header">
              <Typography.Title level={5} style={{ paddingTop: '8px' }}>
                Trials
              </Typography.Title>
              <span style={{ padding: '0 16px' }}>
                <VerticalDividerIcon />
              </span>
              <Input.Search
                placeholder="Search by trial name"
                size="middle"
                enterButton={<SearchUniversal twoToneColor={'#FFF'} />}
                onSearch={onSearch}
                style={{
                  width: 300,
                }}
              />
            </div>
            <Table
              locale={{ emptyText: error && 'No trials data available' }}
              size="large"
              style={{
                border: '1px solid #E8E8E8',
                background: '#FFFFFF',
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
                cursor: 'pointer',
              }}
              columns={[
                AdaptiveExperimentNameColumn,
                AdaptiveExperimentDurationColumn,
                AdaptiveExperimentStatusColumn,
                AdaptiveExperimentStartDate,
                AdaptiveExperimentEndDate,
              ]}
              dataSource={uniqueTrialData}
              loading={isLoading}
              rowKey={(record) => record?.arm_id + record?.trial_name}
              scroll={{ x: 1300 }}
              onRow={(record) => {
                return {
                  onClick: (_) => {
                    const trialName = record?.trial_name;
                    const allTrials = partitionData[trialName];

                    setAllTrialsByTrialName(allTrials);
                    setOpenTrialDrawer(true);
                  },
                };
              }}
            />
          </div>
          <div>
            <CardCollapse
              title="Experiment Setup"
              subtitle="This is the JSON view for the adaptive experiment"
              defaultOpen={true}
              collapseProps={{ bordered: true }}
            >
              <JsonView
                data={adaptiveExperiment}
                shouldInitiallyExpand={(level) => (level < 2 ? true : false)}
                style={customStyles}
              />
            </CardCollapse>
          </div>
        </Flex>
      </ObjectDetailPage>
    );
  }

  return renderAdaptiveDetail();
}

const customStyles = {
  ...darkStyles,
  container: 'dark',
  label: 'label',
  stringValue: 'string',
  numberValue: 'number',
  booleanValue: 'boolean',
  nullValue: 'null',
  punctuation: 'punctuation',
  undefined: 'undefined',
};

const renderAdaptiveNameColumn = (name: string, record: AdaptiveTrial) => {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center' }}
      key={record?.arm_id + name}
    >
      <AdaptiveIcon />
      <div style={{ paddingLeft: '12px' }}>
        <Typography.Text>{record?.trial_name}</Typography.Text>
      </div>
    </div>
  );
};

const renderAdaptiveDurationColumn = (state: string, record: AdaptiveTrial) => {
  const [startDate, endDate] = [
    transformMilliSecondTime(record?.start_time),
    transformMilliSecondTime(record?.end_time),
  ];
  const daysDifference = calculateDaysDifference(startDate, endDate);
  const displayProgress = transformProgress(state, startDate, endDate);
  const completedProgess = extractCompletionProgress(startDate, endDate);

  if (!daysDifference) {
    return <SubtractUniversal />;
  }

  return (
    <div>
      {displayProgress}
      {completedProgess}/
      <Typography.Text key={daysDifference}>{daysDifference}</Typography.Text>
    </div>
  );
};

const AdaptiveExperimentNameColumn: any = {
  key: 'arm_id',
  title: 'Name',
  dataIndex: 'adaptive_experiment_name',
  fixed: 'left',
  width: 400,
  render: (name: string, record: AdaptiveTrial) =>
    renderAdaptiveNameColumn(name, record),
};

const AdaptiveExperimentStatusColumn: any = {
  key: 'status',
  title: 'Status',
  dataIndex: 'state',
  sorter: (a: AdaptiveTrial, b: AdaptiveTrial) =>
    sortString(a?.state, b?.state),
  width: 280,
  render: (state: string) => renderStatusColumn(state),
};

const AdaptiveExperimentDurationColumn: any = {
  key: 'duration',
  title: 'Duration',
  dataIndex: 'state',
  width: 280,
  render: (state: string, record: AdaptiveTrial) =>
    renderAdaptiveDurationColumn(state, record),
};

export const AdaptiveExperimentStartDate: any = {
  key: 'startDate',
  title: 'Start Date',
  dataIndex: 'start_time',
  width: 280,
  render: (startTime: number) => {
    const dateTime = transformMilliSecondTime(startTime);
    if (!dateTime) {
      return <SubtractUniversal />;
    }
    return (
      <>
        <Typography.Text>{dateTime}</Typography.Text>{' '}
        <Typography.Text type="secondary">
          {getTimezoneDisplay()}
        </Typography.Text>
      </>
    );
  },
};

export const AdaptiveExperimentEndDate: any = {
  key: 'endDate',
  title: 'End Date',
  dataIndex: 'end_time',
  width: 280,
  render: (endTime: number) => {
    const dateTime = transformMilliSecondTime(endTime);
    if (!dateTime) {
      return <SubtractUniversal />;
    }

    return (
      <>
        <Typography.Text>{dateTime}</Typography.Text>{' '}
        <Typography.Text type="secondary">
          {getTimezoneDisplay()}
        </Typography.Text>
      </>
    );
  },
};

export function AdaptiveTrialsCount({
  experimentName,
}: {
  experimentName: string;
}) {
  const [adaptiveName] = experimentName?.split('.') || [];
  const { isLoading, data } = useQuery({
    queryKey: ['trials', adaptiveName],
    queryFn: () => {
      return Service.planner.fetchAdaptiveExperimentTrials(adaptiveName);
    },
    retry: 3,
  });

  if (isLoading) {
    return <Spin style={{ margin: '0 4px' }} />;
  }

  const count = data ? data?.length : 0;
  return (
    <Tag size="xsmall" color="#F1EEFF" style={{ margin: '0 4px' }}>
      <span style={{ color: '#8B74FF' }}>{count} Trials</span>
    </Tag>
  );
}
